import React from 'react';
import PropTypes from 'prop-types';
import { Modal, Form, Input, DatePicker, Typography, Alert, Divider } from 'antd';
import moment from 'moment';

const { Text } = Typography;

const formatINR = (value) =>
	`₹${Number(value || 0).toLocaleString('en-IN', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})}`;

/**
 * BulkPaymentModal
 *
 * Collects a single shared UTR number + paid date for a bulk "mark as paid"
 * action across multiple selected vendor invoices. Each selected invoice is
 * paid for its full remaining balance on the backend, so no per-invoice amount
 * is collected here — but the per-invoice amount and the batch total ARE shown
 * so the user can confirm how much is about to be paid.
 *
 * `invoices`: [{ id, vendor_bill_no, amount }] — amount = remaining balance to be paid.
 */
export default function BulkPaymentModal({ visible, invoices, loading, onClose, onSubmit }) {
	const [form] = Form.useForm();

	const count = invoices.length;
	const totalAmount = invoices.reduce((sum, inv) => sum + Number(inv.amount || 0), 0);

	const handleOk = async () => {
		try {
			const values = await form.validateFields();
			await onSubmit({
				utr_number: values.utr_number.trim(),
				paid_date: moment(values.paid_date).format('YYYY-MM-DD'),
			});
			form.resetFields();
		} catch (err) {
			// validateFields throws on invalid form; nothing else to do.
		}
	};

	const handleCancel = () => {
		form.resetFields();
		onClose();
	};

	return (
		<Modal
			title="Mark Invoices as Paid"
			open={visible}
			onOk={handleOk}
			onCancel={handleCancel}
			confirmLoading={loading}
			okText="Mark as Paid"
			destroyOnClose
		>
			<Alert
				type="info"
				showIcon
				style={{ marginBottom: 16 }}
				message={
					<Text>
						<b>{count}</b> invoice{count === 1 ? '' : 's'} selected. Each will be paid
						its full remaining balance using the UTR and date below.
					</Text>
				}
			/>

			<div
				style={{
					maxHeight: 180,
					overflowY: 'auto',
					border: '1px solid #f0f0f0',
					borderRadius: 4,
					padding: '4px 12px',
					marginBottom: 12,
				}}
			>
				{invoices.map((inv) => (
					<div
						key={inv.id}
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							padding: '6px 0',
							borderBottom: '1px solid #fafafa',
						}}
					>
						<Text ellipsis style={{ marginRight: 12 }}>
							{inv.vendor_bill_no || `Invoice #${inv.id}`}
						</Text>
						<Text strong>{formatINR(inv.amount)}</Text>
					</div>
				))}
			</div>

			<Divider style={{ margin: '8px 0' }} />
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: 16,
				}}
			>
				<Text strong>Total to be paid</Text>
				<Text strong style={{ fontSize: 16, color: '#091744' }}>
					{formatINR(totalAmount)}
				</Text>
			</div>

			<Form form={form} layout="vertical" preserve={false}>
				<Form.Item
					name="utr_number"
					label="UTR Number"
					rules={[
						{ required: true, message: 'UTR Number is required' },
						{
							validator: (_, value) =>
								value && value.trim()
									? Promise.resolve()
									: Promise.reject(new Error('UTR Number is required')),
						},
					]}
				>
					<Input placeholder="Enter UTR number" allowClear />
				</Form.Item>
				<Form.Item
					name="paid_date"
					label="Paid Date"
					rules={[{ required: true, message: 'Paid Date is required' }]}
				>
					<DatePicker
						style={{ width: '100%' }}
						format="DD/MM/YYYY"
						disabledDate={(current) => current && current > moment().endOf('day')}
					/>
				</Form.Item>
			</Form>
		</Modal>
	);
}

BulkPaymentModal.propTypes = {
	visible: PropTypes.bool,
	invoices: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
			vendor_bill_no: PropTypes.string,
			amount: PropTypes.number,
		})
	),
	loading: PropTypes.bool,
	onClose: PropTypes.func.isRequired,
	onSubmit: PropTypes.func.isRequired,
};

BulkPaymentModal.defaultProps = {
	visible: false,
	invoices: [],
	loading: false,
};
